<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3"
import { computed, ref } from "vue"
import route from "ziggy-js"
import { CalendarDays, ChefHat, ChevronLeft, ChevronRight, Coffee, CreditCard, Pencil, Plus, Trash2 } from "lucide-vue-next"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/toast/use-toast"
import { toHuman } from "@/helpers/formatters/currency"
import AppLayout from "@/layouts/AppLayout.vue"
import AssignShiftDialog from "./AssignShiftDialog.vue"
import ScheduleSummary, { type ScheduleRange } from "./ScheduleSummary.vue"

defineOptions({
    layout: [AppLayout],
})

type Employee = {
    id: number
    name: string
    title: string
    branch_id: number | null
}

type Shift = {
    id: number
    station: string
    employee: Employee
    start: string
    end: string
    start_label: string
    end_label: string
    is_holiday: boolean
    logged_in_label: string | null
    expected_login_label: string | null
    login_status: "on_time" | "late" | "pending"
    covers_opening: boolean
    total_hours: number
    total_pay_in_cents: number
}

type Station = {
    value: string
    label: string
    note: string | null
    shifts: Shift[]
}

type WeekDay = {
    date: string
    weekday: string
    day: string
    is_today: boolean
    is_selected: boolean
    count: number
}

type Payroll = {
    employee: { id: number; name: string; title: string }
    hours: number
    salary: number
}

type StoreHours = {
    opens_at: string
    closes_at: string
    opens_label: string
    closes_label: string
    staff_login_lead_minutes: number
    opening_login_label: string
    default_start: string
    default_end: string
}

type Props = {
    scheduleRange: ScheduleRange
    date: string
    dateLabel: string
    branchId: number
    storeHours: StoreHours
    weekDays: WeekDay[]
    stations: Station[]
    employees: Employee[]
    scheduledEmployeeIds: number[]
    payrolls: Payroll[]
    links: {
        index_path: string
        store_path: string
    }
}

const props = defineProps<Props>()
const page = usePage()
const { toast } = useToast()

const branches = computed(() => (page.props.branches as { id: number; name: string }[]) ?? [])
const hasMultipleBranches = computed(() => branches.value.length > 1)

const dialogOpen = ref(false)
const dialogStation = ref("barista")
const editingShift = ref<Shift | null>(null)
const dialogEmployeeId = ref<number | null>(null)

const assignedCount = computed(() => props.stations.reduce((total, station) => total + station.shifts.length, 0))
const dayPay = computed(() => props.payrolls.reduce((total, payroll) => total + payroll.salary, 0))

const unassignedEmployees = computed(() => {
    const scheduled = new Set(props.scheduledEmployeeIds)

    return props.employees.filter((employee) => !scheduled.has(employee.id))
})

const stationIcons: Record<string, typeof Coffee> = {
    barista: Coffee,
    cashier: CreditCard,
    kitchen: ChefHat,
}

function initials(name: string) {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("")
}

function visit(nextDate: string, branchId = props.branchId) {
    if (!nextDate) {
        return
    }

    router.get(
        props.links.index_path,
        { date: nextDate, branch_id: branchId, range_start: props.scheduleRange.start, range_end: props.scheduleRange.end },
        { preserveState: true, preserveScroll: true }
    )
}

function shiftDate(current: string, days: number) {
    const next = new Date(`${current}T12:00:00`)
    next.setDate(next.getDate() + days)

    return next.toISOString().slice(0, 10)
}

function todayDate() {
    const now = new Date()

    return [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
    ].join("-")
}

function openAssign(station: string, shift: Shift | null = null, employeeId: number | null = null) {
    dialogStation.value = shift?.station ?? station
    editingShift.value = shift
    dialogEmployeeId.value = employeeId
    dialogOpen.value = true
}

function destroyShift(shift: Shift) {
    if (!confirm(`Remove ${shift.employee.name} from ${shift.station}?`)) {
        return
    }

    router.delete(route("shifts.destroy", shift.id), {
        preserveScroll: true,
        onSuccess() {
            toast({ description: "Shift removed." })
        },
    })
}

function loginLabel(shift: Shift) {
    if (shift.login_status === "pending") {
        return `Log in by ${shift.expected_login_label}`
    }

    if (shift.login_status === "on_time") {
        return `In at ${shift.logged_in_label}`
    }

    return shift.logged_in_label ? `Late · ${shift.logged_in_label}` : "Late"
}

function loginClass(status: Shift["login_status"]) {
    return {
        pending: "bg-slate-100 text-slate-700 border-slate-200",
        on_time: "bg-emerald-100 text-emerald-800 border-emerald-200",
        late: "bg-red-100 text-red-800 border-red-200",
    }[status]
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8">
        <div class="bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <p class="text-sm font-medium uppercase tracking-[0.2em] text-white/70">Staffing</p>
                        <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Daily Schedule</h1>
                        <p class="mt-2 max-w-2xl text-white/80">
                            Store hours {{ storeHours.opens_label }} – {{ storeHours.closes_label }}. Staff log in
                            {{ storeHours.staff_login_lead_minutes }} minutes early for opening
                            ({{ storeHours.opening_login_label }}). Later is late.
                        </p>
                    </div>

                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <Select
                            v-if="hasMultipleBranches"
                            :model-value="String(branchId)"
                            @update:model-value="visit(date, Number($event))"
                        >
                            <SelectTrigger class="w-[200px] border-white/20 bg-white/10 text-white">
                                <SelectValue placeholder="Select branch" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="branch in branches" :key="branch.id" :value="String(branch.id)">
                                    {{ branch.name }}
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <div class="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                class="text-white hover:bg-white/15 hover:text-white"
                                @click="visit(shiftDate(date, -1))"
                            >
                                <ChevronLeft class="h-4 w-4" />
                            </Button>
                            <Input
                                type="date"
                                :model-value="date"
                                class="w-[160px] border-white/20 bg-white/10 text-white"
                                @update:model-value="visit(String($event))"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                class="text-white hover:bg-white/15 hover:text-white"
                                @click="visit(shiftDate(date, 1))"
                            >
                                <ChevronRight class="h-4 w-4" />
                            </Button>
                            <Button
                                type="button"
                                class="bg-white text-[rgb(181_115_5)] hover:bg-white/90"
                                @click="visit(todayDate())"
                            >
                                Today
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="sticky top-0 z-10 border-b bg-[#f3f1ea]/95 backdrop-blur">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="flex gap-1 overflow-x-auto py-3">
                    <button
                        v-for="day in weekDays"
                        :key="day.date"
                        type="button"
                        class="min-w-[4.5rem] rounded-xl px-3 py-2 text-center transition-colors"
                        :class="
                            day.is_selected
                                ? 'bg-[rgb(181_115_5)] text-white'
                                : 'text-gray-600 hover:bg-white'
                        "
                        @click="visit(day.date)"
                    >
                        <span class="block text-[11px] font-medium uppercase tracking-wide">{{ day.weekday }}</span>
                        <span class="block text-lg font-semibold leading-none">{{ day.day }}</span>
                        <span class="mt-1 block text-[10px] opacity-80">
                            {{ day.count }} {{ day.count === 1 ? "shift" : "shifts" }}
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <div class="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <ScheduleSummary
                :range="scheduleRange"
                :date="date"
                :branch-id="branchId"
                :branch-name="branches.find((branch) => branch.id === branchId)?.name ?? `Branch ${branchId}`"
                :index-path="links.index_path"
            />

            <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 class="text-xl font-semibold text-gray-900">{{ dateLabel }}</h2>
                    <p class="text-sm text-muted-foreground">
                        {{ assignedCount }} assigned · {{ unassignedEmployees.length }} available
                    </p>
                </div>
                <Button @click="openAssign('barista')">
                    <Plus class="mr-1 h-4 w-4" />
                    Assign employee
                </Button>
            </div>

            <div class="grid gap-4 xl:grid-cols-[1fr_280px]">
                <div class="grid gap-4 md:grid-cols-3">
                    <Card v-for="station in stations" :key="station.value" class="overflow-hidden">
                        <CardHeader class="space-y-1 pb-3">
                            <div class="flex items-center justify-between gap-2">
                                <CardTitle class="flex items-center gap-2 text-base">
                                    <component :is="stationIcons[station.value]" class="h-4 w-4 text-[rgb(181_115_5)]" />
                                    {{ station.label }}
                                </CardTitle>
                                <span class="text-xs font-medium text-muted-foreground">
                                    {{ station.shifts.length }}
                                </span>
                            </div>
                            <CardDescription v-if="station.note">{{ station.note }}</CardDescription>
                        </CardHeader>
                        <CardContent class="space-y-3">
                            <div
                                v-for="shift in station.shifts"
                                :key="shift.id"
                                class="rounded-lg border bg-white p-3"
                            >
                                <div class="flex items-start gap-3">
                                    <Avatar class="h-9 w-9">
                                        <AvatarFallback>{{ initials(shift.employee.name) }}</AvatarFallback>
                                    </Avatar>
                                    <div class="min-w-0 flex-1">
                                        <p class="truncate text-sm font-semibold">{{ shift.employee.name }}</p>
                                        <p class="text-xs text-muted-foreground">
                                            {{ shift.start_label }} – {{ shift.end_label }}
                                            <span v-if="shift.is_holiday"> · Holiday</span>
                                        </p>
                                        <span
                                            v-if="shift.login_status"
                                            class="mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium"
                                            :class="loginClass(shift.login_status)"
                                        >
                                            {{ loginLabel(shift) }}
                                        </span>
                                    </div>
                                    <div class="flex shrink-0">
                                        <Button variant="ghost" size="icon" class="h-8 w-8" @click="openAssign(station.value, shift)">
                                            <Pencil class="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" class="h-8 w-8 text-destructive" @click="destroyShift(shift)">
                                            <Trash2 class="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <p v-if="station.shifts.length === 0" class="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                                No one assigned
                            </p>

                            <Button variant="outline" class="w-full" @click="openAssign(station.value)">
                                <Plus class="mr-1 h-4 w-4" />
                                Assign
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                <div class="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle class="text-base">Today's pay</CardTitle>
                            <CardDescription>{{ toHuman(dayPay / 100) }} across {{ payrolls.length }} staff</CardDescription>
                        </CardHeader>
                        <CardContent class="space-y-4">
                            <div v-if="payrolls.length === 0" class="text-sm text-muted-foreground">
                                No shifts scheduled for this day.
                            </div>
                            <div v-for="payroll in payrolls" :key="payroll.employee.id" class="flex items-center gap-3">
                                <Avatar class="h-8 w-8">
                                    <AvatarFallback>{{ initials(payroll.employee.name) }}</AvatarFallback>
                                </Avatar>
                                <div class="min-w-0 flex-1">
                                    <p class="truncate text-sm font-medium">{{ payroll.employee.name }}</p>
                                    <p class="text-xs text-muted-foreground">{{ payroll.hours }} hrs · {{ payroll.employee.title }}</p>
                                </div>
                                <div class="text-sm font-medium">{{ toHuman(payroll.salary / 100) }}</div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle class="flex items-center gap-2 text-base">
                                <CalendarDays class="h-4 w-4" />
                                Available
                            </CardTitle>
                            <CardDescription>Active baristas not on today's board</CardDescription>
                        </CardHeader>
                        <CardContent class="space-y-2">
                            <p v-if="unassignedEmployees.length === 0" class="text-sm text-muted-foreground">
                                Everyone is already assigned.
                            </p>
                            <button
                                v-for="employee in unassignedEmployees"
                                :key="employee.id"
                                type="button"
                                class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                                @click="openAssign('barista', null, employee.id)"
                            >
                                <span class="truncate font-medium">{{ employee.name }}</span>
                                <span class="ml-2 shrink-0 text-xs text-muted-foreground">{{ employee.status }}</span>
                            </button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>

        <AssignShiftDialog
            v-model:open="dialogOpen"
            :date="date"
            :station="dialogStation"
            :employees="employees"
            :store-hours="storeHours"
            :shift="editingShift"
            :employee-id="dialogEmployeeId"
            :store-path="links.store_path"
        />
    </div>
</template>
